import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { createCustomStateSetOnUrlHandler } from '@appState/helpers/state-reset-handler';
import { getAll, getOne, StateDictionary } from '@appState/helpers/store-base';
import {
  CreateEmpoweredProductLicenseRequest,
  EmpoweredProductLicense,
  EmpoweredProductLicenseResponse,
  GenerateEmpoweredProductLicenseKeyRequest,
  SetEmpoweredProductLicenseKeyRequest,
  UpdateEmpoweredProductLicenseRequest,
  UpdateEmpoweredProductLicenseStatusRequest,
} from '@models/products-and-orders/requests/empowered-product-license.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { RequestsStore } from '../requests.store';
import { EmpoweredProductLicensesService } from './empowered-product-licenses.service';

type EmpoweredProductLicensesState = {
  empoweredLicenses: EmpoweredProductLicense[];
  isLoading: boolean;

  isCreateLoading: boolean;
  isSelectedLicenseLoading: boolean;
  selectedLicense: EmpoweredProductLicenseResponse | null;
  isEmpoweredLicenseSaveCompleted: boolean;

  generatedLicenseKey: string | null;
  isGenerateLicenseKeyLoading: boolean;
};

const initialState: EmpoweredProductLicensesState = {
  empoweredLicenses: [],
  isLoading: false,

  isCreateLoading: false,
  isSelectedLicenseLoading: false,
  selectedLicense: null,
  isEmpoweredLicenseSaveCompleted: false,

  generatedLicenseKey: null,
  isGenerateLicenseKeyLoading: false,
};

const licensesDictionary: StateDictionary<EmpoweredProductLicense> = {
  entityList: 'empoweredLicenses',
  listLoading: 'isLoading',
  createLoading: 'isCreateLoading',
  selectedEntity: 'selectedLicense',
  selectedEntityLoading: 'isSelectedLicenseLoading',
  entityId: 'id',
};

export const EmpoweredProductLicensesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('empowered-product-licenses-store'),
  withComputed(store => ({
    selectedLicenseId: computed(() => store.selectedLicense()?.id || null),
  })),
  withMethods(
    (
      store,
      service = inject(EmpoweredProductLicensesService),
      notificationsService = inject(NotificationsService),
      router = inject(Router),
      requestsStore = inject(RequestsStore),
    ) => {
      createCustomStateSetOnUrlHandler<EmpoweredProductLicensesState>('/requests', { selectedLicense: null })(store);
      createCustomStateSetOnUrlHandler<EmpoweredProductLicensesState>('/empowered-licenses', { selectedLicense: null })(store);

      const repositoryMethods = {
        getAll: getAll<EmpoweredProductLicense>({
          store,
          service,
          dictionary: licensesDictionary,
        }),
        get: getOne<EmpoweredProductLicenseResponse>({
          store,
          service,
          dictionary: licensesDictionary,
        }),

        create: rxMethod<CreateEmpoweredProductLicenseRequest>(
          pipe(
            tap(() => {
              patchState(store, { isCreateLoading: true });
            }),
            switchMap(request =>
              service.create(request).pipe(
                tap(selectedLicense => {
                  patchState(store, { selectedLicense, isCreateLoading: false });
                  requestsStore.loadRequests();
                  requestsStore.getOne(selectedLicense.productRequestId);
                  notificationsService.showNotification('Request created: please review the details.');
                  router.navigate(['requests', selectedLicense.productRequestId, 'details']);
                }),
                catchError(() => {
                  patchState(store, { isCreateLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        update: rxMethod<{ id: string; dto: UpdateEmpoweredProductLicenseRequest; isDraft: boolean; isRequestsView: boolean }>(
          pipe(
            tap(() => {
              patchState(store, { isSelectedLicenseLoading: true, isEmpoweredLicenseSaveCompleted: false });
            }),
            switchMap(({ id, dto, isDraft, isRequestsView }) =>
              service.update(id, dto).pipe(
                tap(selectedLicense => {
                  patchState(store, {
                    selectedLicense,
                    isSelectedLicenseLoading: false,
                    isEmpoweredLicenseSaveCompleted: !isDraft,
                  });
                  if (isRequestsView) {
                    requestsStore.loadRequests();
                  } else {
                    patchState(store, {
                      empoweredLicenses: store.empoweredLicenses().map(l => (l.id === selectedLicense.id ? selectedLicense : l)),
                    });
                  }
                }),
                catchError(() => {
                  patchState(store, { isSelectedLicenseLoading: false, isEmpoweredLicenseSaveCompleted: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        updateStatus: rxMethod<{ id: string; data: UpdateEmpoweredProductLicenseStatusRequest; isRequestsView: boolean }>(
          pipe(
            tap(() => {
              patchState(store, { isSelectedLicenseLoading: true });
            }),
            switchMap(({ id, data, isRequestsView }) =>
              service.updateStatus(id, data).pipe(
                tap(selectedLicense => {
                  patchState(store, { selectedLicense, isSelectedLicenseLoading: false });
                  const getMessage = (status: string) => {
                    switch (status) {
                      case 'Submitted':
                        return 'Request successfully submitted.';
                      case 'Canceled':
                        return 'Request successfully canceled.';
                      default:
                        return '';
                    }
                  };
                  notificationsService.showNotification(getMessage(selectedLicense.status));
                  if (isRequestsView) {
                    router.navigate(['requests']);
                    requestsStore.loadRequests();
                  } else {
                    router.navigate(['empowered-licenses']);
                    repositoryMethods.getAll();
                  }
                }),
                catchError(() => {
                  patchState(store, { isSelectedLicenseLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        delete: rxMethod<{ id: string; isRequestsView: boolean }>(
          pipe(
            tap(() => {
              patchState(store, { isSelectedLicenseLoading: true });
            }),
            switchMap(({ id, isRequestsView }) =>
              service.delete(id).pipe(
                tap(() => {
                  patchState(store, { selectedLicense: null, isSelectedLicenseLoading: false });
                  if (isRequestsView) {
                    requestsStore.loadRequests();
                  } else {
                    patchState(store, { empoweredLicenses: store.empoweredLicenses().filter(l => l.id !== id) });
                  }
                }),
                catchError(() => {
                  patchState(store, { isSelectedLicenseLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        generateLicenseKey: rxMethod<{ request: GenerateEmpoweredProductLicenseKeyRequest }>(
          pipe(
            tap(() => patchState(store, { generatedLicenseKey: null, isGenerateLicenseKeyLoading: true })),
            switchMap(({ request }) =>
              service.generateLicenseKey(request).pipe(
                tap(response => {
                  patchState(store, { generatedLicenseKey: response.licenseKey, isGenerateLicenseKeyLoading: false });
                }),
                catchError(() => {
                  patchState(store, { generatedLicenseKey: null, isGenerateLicenseKeyLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        setLicenseKey: rxMethod<{ id: string; dto: SetEmpoweredProductLicenseKeyRequest; isRequestsView: boolean }>(
          pipe(
            tap(() => {
              patchState(store, { isSelectedLicenseLoading: true });
            }),
            switchMap(({ id, dto, isRequestsView }) =>
              service.setLicenseKey(id, dto).pipe(
                tap(selectedLicense => {
                  patchState(store, { selectedLicense, isSelectedLicenseLoading: false });
                  notificationsService.showNotification('License key set successfully.');
                  if (isRequestsView) {
                    requestsStore.loadRequests();
                  } else {
                    patchState(store, {
                      empoweredLicenses: store.empoweredLicenses().map(l => (l.id === selectedLicense.id ? selectedLicense : l)),
                    });
                  }
                }),
                catchError(() => {
                  patchState(store, { isSelectedLicenseLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const navigationMethods = {
        resetSaveCompleted: () => {
          patchState(store, { isEmpoweredLicenseSaveCompleted: false });
        },
        resetGenerateLicenseKey: () => {
          patchState(store, { generatedLicenseKey: null, isGenerateLicenseKeyLoading: false });
        },
        goToEmpoweredLicenseActions: async (id: string): Promise<boolean> => {
          const result = await router.navigate(['empowered-licenses', id]);

          if (!result) return false;

          if (!store.isSelectedLicenseLoading()) repositoryMethods.get(id);

          return true;
        },
      };

      return { ...repositoryMethods, ...navigationMethods };
    },
  ),
);

export interface EmpoweredProductLicensesStoreType {
  readonly empoweredLicenses: Signal<EmpoweredProductLicense[]>;
  readonly isLoading: Signal<boolean>;
  readonly isCreateLoading: Signal<boolean>;
  readonly isSelectedLicenseLoading: Signal<boolean>;
  readonly selectedLicense: Signal<EmpoweredProductLicenseResponse | null>;
  readonly isEmpoweredLicenseSaveCompleted: Signal<boolean>;

  readonly generatedLicenseKey: Signal<string | null>;
  readonly isGenerateLicenseKeyLoading: Signal<boolean>;

  readonly selectedLicenseId: Signal<string | null>;

  readonly getAll: () => void;
  readonly get: (id: string) => void;
  readonly create: (request: CreateEmpoweredProductLicenseRequest) => void;
  readonly update: (params: { id: string; dto: UpdateEmpoweredProductLicenseRequest; isDraft: boolean; isRequestsView: boolean }) => void;
  readonly updateStatus: (params: { id: string; data: UpdateEmpoweredProductLicenseStatusRequest; isRequestsView: boolean }) => void;
  readonly delete: (params: { id: string; isRequestsView: boolean }) => void;
  readonly generateLicenseKey: (input: { request: GenerateEmpoweredProductLicenseKeyRequest }) => void;
  readonly setLicenseKey: (params: { id: string; dto: SetEmpoweredProductLicenseKeyRequest; isRequestsView: boolean }) => void;
  readonly resetSaveCompleted: () => void;
  readonly resetGenerateLicenseKey: () => void;
  readonly goToEmpoweredLicenseActions: (id: string) => Promise<boolean>;
}
